/** @import "./../../common/types" */

import { isEqual, uniqWith } from 'lodash'
import { nanoid } from 'nanoid'
import * as React from 'react'

/**
 * @function
 * ドロアーの高さを計算
 * @returns { {drawerRef:ReturnType<React.useRef>,drawerWidth:number }}
 */

export const useGetDrawerBounds = () => {
  const drawerRef = React.useRef(null)
  //ドロアー幅
  const drawerWidth = 485

  React.useEffect(() => {
    if (!(window && document)) return

    function onResize() {
      const header = document.querySelector('#header')
      if (!header) return

      const bounds = header.getBoundingClientRect()
      drawerRef.current.style.setProperty('--header-height', `${bounds.height + 4}px`)
    }

    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return { drawerRef, drawerWidth }
}

/**
 * @function
 * リストをマップに変換
 * @param  {any[]} list
 * @returns {Record<string, any>}
 */
export const useConvertListToMap = (list) =>
  React.useMemo(() => Object.fromEntries(list.map((item) => [item.id, item])), [list])

export const FilterContext = React.createContext({})

/**
 * フィルター状態
 * @returns {UseFilterReturnType}
 */
export const useFilter = () => React.useContext(FilterContext)

const maxLevel = 6

/**
 * @function
 * @param { string[][]} main
 * @param { [number,number]} index // 行, 列
 */
const createDescendants = (main, [row, col]) => {
  // Pattern A-B-C
  //子孫の取得
  const slice = main[row]
    .slice(col + 1, main[row].length)
    .filter((x) => x !== '-')
    .map((x, index) => [x, col + 1 + index])

  return slice
}

const createData = ({ tempMap, name, slice, info }) => {
  if (!tempMap?.[name]) {
    tempMap[name] = {
      id: nanoid(),
      name,
      descendents: slice,
      selected: true,
      show: true,
      info: info.join(' > ')
    }
  } else {
    const node = tempMap[name]

    const descendents = uniqWith([...node.descendents, ...slice], isEqual)

    if (node.descendents)
      tempMap[name] = {
        ...node,
        descendents
      }
  }

  return tempMap
}

//ーーA　パターン
const createRefs = ({ perLevel, col, row, main, info, name }) =>
  new Promise((resolve) => {
    //階位階層で参照のためのロジックです
    const currentLevel = perLevel[col][name]
    const isRowMinus = info[row][0] === '-'
    const infoVal = info[row][col]
    const mainVal = main[row][col]

    const refTargets = []

    if (isRowMinus) {
      //列の最初の値が-な場合　--A パターンです

      if (infoVal !== mainVal) {
        //  |   -  |   -  |  A  | Original
        //  | (3)A | (3)A |  A  | Filter
        //  一致しないパターン
        for (let counter = 0; counter <= maxLevel && main[row][counter] !== '-'; counter++) {
          if (counter !== col) refTargets.push([main[row][counter], counter])
        }
      } else {
        //  |   -  |   -  |  A  | Original
        //  | (3)A | (3)A |  A  | Filtered
        //一致するパターンで 3番列が対象
        for (let counter = col - 1; counter >= 0; counter--) {
          refTargets.push([main[row][counter], counter])
        }
      }
    }

    if (!isRowMinus && infoVal === '-') {
      // | A | -  | B | - |
      // | A |(1)A| B | - |
      //  2番列が対象
      for (let counter = 0; counter <= maxLevel && main[row][counter] !== '-'; counter++) {
        if (counter !== col) refTargets.push([main[row][counter], counter])
      }
      // こちらは捨ててもいいと思いますが念のため
      refTargets.push([main[row][0], 0])
    }

    if (refTargets.length > 0) {
      perLevel[col][name] = { ...currentLevel, info: '', refTargets }
    }
    resolve()
  })

// 組織の処理
export const processDepartments = (main = [], info = []) => {
  if ([main, info].some((e) => e.length === 0)) {
    console.error('フィルター項目がありません')
    return {}
  }

  //階層ごとの組織データ
  const perLevel = new Array(info[0].length || 0).fill(null).map((_) => ({}))

  for (let col = 0; col < main[0].length; col++) {
    const tempMap = {}

    for (let row = 0; row < main.length; row++) {
      const name = main[row][col]
      if (name === '-') continue

      const ancestors = main[row].slice(0, col)
      let slice = createDescendants(main, [row, col])

      perLevel[col] = createData({
        tempMap,
        name,
        slice: slice,
        info: ancestors
      })

      createRefs({ perLevel, col, row, main, info, name })
    }
  }

  return perLevel
}
